<template>
  <IbtRegisterForm
    :title="$t('verificationStatus.ibtVerification.financialInformation.title')"
    :desc="$t('verificationStatus.ibtVerification.financialInformation.desc')"
    :nextButtonOnClick="nextButtonOnClick"
  >
    <el-form class="mt-10 flex flex-col items-stretch justify-start gap-1" :model="form" :rules="rules" ref="form">
      <div v-for="(question, index) in filteredFormQuestions" :key="question.questionId">
        <el-form-item :prop="`${question.questionId}`" class="m-0" :inline-message="true">
          <template v-if="question.questionId===300">
            <div class="el-form-item__label m-0 p-0">{{ `${index + 1}. ${question.desc}` }}</div>
            <el-select  v-model="form[question.questionId]" filterable :placeholder="$t('common.formValidation.answerReq')" class="showPlaceholder">
              <el-option
                v-for="questionOption in question.questionOptions"
                :key="questionOption.id"
                :label="questionOption.desc"
                :value="questionOption.id">
              </el-option>
            </el-select>
          </template>
          <template v-else>
            <el-radio-group
              class="w-full"
              :data-testid="question.pkey ? `question_${question.pkey.replaceAll('.', '')}` : ''"
              v-model="form[question.questionId]"
              @input="onSelectChange(index)"
            >
              <RegisterCollapseRow
                :title="`${index + 1}. ${question.desc}`"
                :expandable="true"
                :answerLabel="getQuestionAnswerDesc(question.questionOptions, form[question.questionId])"
                :defaultCollapse="question.collapse"
                expandLabel=""
                maxHeightTransition="max-h-0"
                :ref="`collapseRowRef${index}`"
                >
                <template>
                  <div class="flex flex-col items-start justify-start gap-5 mb-5">
                    <el-radio
                      v-for="questionOption in question.questionOptions"
                      :key="questionOption.id"
                      :label="questionOption.id"
                      :data-testid="questionOption.pkey ? `answer_${questionOption.pkey.replaceAll('.', '')}` : null"
                    >
                      {{ questionOption.desc }}</el-radio
                    >
                  </div>
                  </template>
                </RegisterCollapseRow>
            </el-radio-group>
          </template>
        </el-form-item>
      </div>
    </el-form>
  </IbtRegisterForm>
</template>

<script>
import IbtRegisterForm from '@/components/register/registerForms/IbtRegisterForm.vue';
import RegisterCollapseRow from '@/components/register/RegisterCollapseRow.vue';

import RegisterCheckButton from '@/components/register/RegisterCheckButton.vue';
import ibtStep from '@/constants/register/ibtStep';
import ibtStore from '@/store/register/ibtVerification/ibtStore';
import { mapGetters, mapState } from 'vuex';
import ibtRepository from '@/repositories/register/ibtRepository';
import auditStatus, { getAuditStatusByString } from '@/constants/auditStatus';
import { sensorsTrack } from '@/util/sensorsdata';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';

export default {
  name: 'FinancialInformation',
  components: {
    IbtRegisterForm,
    RegisterCollapseRow,
    RegisterCheckButton
  },
  data() {
    return {
      occupationQuestion: null,
      employmentQuestionId: null,
      employedAnswerIds: [],
      // For question ordering
      filteredFormQuestions: [],
      formQuestions: [],
      // For form validation
      form: {},
      rules: {}
    };
  },
  computed: {
    isOccupationShow() {
      return this.employedAnswerIds.includes(this.form[this.employmentQuestionId] ?? null);
    },
    ...mapState('common', ['lang']),
    ...mapGetters('common', ['isIbtPoiUnderReview', 'isIbtPoaUnderReview', 'isIbtPoiEditable', 'isIbtPoaEditable']),
    ...mapState('register', ['employmentFinanceQuestions'])
  },
  watch: {
    lang() {
      this.getQuestions();
    },
    employmentFinanceQuestions(employmentFinanceQuestions) {
      this.setFormAndRule(employmentFinanceQuestions);
    },
    isOccupationShow(isOccupationShow) {
      if (isOccupationShow) {
        const occupationAnswer = this.form[this.occupationQuestion.questionId] ?? null;
        // const occupationAnswer =
        //   this.form[this.occupationQuestion.questionId] ?? this.occupationQuestion.questionOptions?.[0]?.id ?? null;

        this.filteredFormQuestions = this.formQuestions.map(e => {
          return { ...e, collapse: occupationAnswer !== null ? true : false };
        });

        this.form[this.occupationQuestion.questionId] = occupationAnswer;
        this.rules[this.occupationQuestion.questionId] = this.requiredRule;
      } else {
        this.filteredFormQuestions = this.formQuestions.filter(
          i => i.questionId !== this.occupationQuestion.questionId
        );
        delete this.rules[this.occupationQuestion.questionId];
      }
    }
  },
  created() {
    this.requiredRule = {
      required: true,
      message: this.$t('common.formValidation.answerReq'),
      trigger: 'change'
    };
  },
  mounted() {
    this.init();
    sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_VIEW, {
      openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV1
    });
  },
  methods: {
    async init() {
      await this.getQuestions();
      const response = await ibtRepository.ibtGetData();
      if (response instanceof Error) {
        this.$message({
          message: response.message,
          type: 'error'
        });
      } else {
        const ibtPoiAuditStatus = getAuditStatusByString(response.ibtPoiAuditStatus);
        const ibtPoaAuditStatus = getAuditStatusByString(response.ibtPoaAuditStatus);

        await Promise.all([
          this.$store.commit('common/setIbtPoiAuditStatus', ibtPoiAuditStatus),
          this.$store.commit('common/setIbtPoaAuditStatus', ibtPoaAuditStatus)
        ]);

        const employmentFinanceAnswers = response.employmentFinanceAnswers;

        if (ibtPoiAuditStatus === auditStatus.completed && ibtPoaAuditStatus === auditStatus.completed) {
          this.$router.push('/depositFunds');
        } else if (employmentFinanceAnswers?.length > 0) {
          if (this.isIbtPoiEditable) {
            ibtStore.dispatch('setStep', ibtStep.confirmId);
          } else if (this.isIbtPoaEditable) {
            ibtStore.dispatch('setStep', ibtStep.confirmPoa);
          } else if (this.isIbtPoiUnderReview || this.isIbtPoaUnderReview) {
            ibtStore.dispatch('setStep', ibtStep.ibtStatus);
          }
        }

        if (employmentFinanceAnswers?.length > 0) {
          const answers = [...employmentFinanceAnswers];

          const form = Object.keys(this.form).reduce((acc, key) => {
            const answer = answers.find(e => `${e.questionId}` === key)?.answers?.[0] ?? null;
            return { ...acc, ...{ [key]: answer } };
          }, {});
          this.form = form;
          this.filteredFormQuestions = this.formQuestions.map(e => {
            const answer = this.form[e.questionId] ?? null;
            return { ...e, collapse: answer !== null ? true : false };
          });
        }
      }
    },
    async getQuestions() {
      await this.$store.dispatch('register/getEmploymentFinanceQuestions', this.lang);
    },
    setFormAndRule(employmentFinanceQuestions) {
      const formQuestions = [...employmentFinanceQuestions].sort((a, b) => Number(a.priority) - Number(b.priority));

      const employmentQuestion = formQuestions.find(i => i.pkey === 'trader.reg2.employmentstatus') ?? null;

      // employedAnswerIds for isOccupationShow watcher
      this.employedAnswerIds =
        employmentQuestion?.questionOptions
          ?.filter(i => i.pkey === 'trader.Employed' || i.pkey === 'trader.Self_employed')
          ?.map(i => i.id) ?? [];

      // employmentQuestionId for isOccupationShow watcher
      this.employmentQuestionId = employmentQuestion?.questionId ?? null;

      var form = {};
      var rules = {};

      // Set form and rules
      formQuestions.forEach(i => {
        if (i.pkey === 'trader.reg2.occupation') {
          this.occupationQuestion = i;
        }
        // form[i.questionId] = i.questionOptions?.[0]?.id ?? null;
        form[i.questionId] = null;
        rules[i.questionId] = this.requiredRule;
      });

      this.formQuestions = formQuestions;
      this.filteredFormQuestions = formQuestions.map(e => {
        return { ...e, collapse: false };
      });
      this.form = form;
      this.rules = rules;
    },
    getQuestionAnswerDesc(questionOptions, answerId) {
      return questionOptions.find(e => e.id === answerId)?.desc ?? '';
    },
    nextButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV1,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV1_PERSONAL_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV1_NEXT
      });
      this.$refs.form?.validate(async valid => {
        if (valid) {
          var employmentFinanceAnswers = [];
          this.employmentFinanceQuestions.forEach(e => {
            const questionId = e.questionId;
            let answer = [this.form[questionId]];

            if (this.form[questionId]) {
              if (questionId === this.occupationQuestion.questionId && !this.isOccupationShow) {
                answer = [];
              }
              employmentFinanceAnswers.push({ questionId: questionId, answers: answer });
            }
          });

          const response = await ibtRepository.submitIbtQuestionnaire({
            employmentFinanceAnswers
          });
          if (response instanceof Error) {
            this.$message({
              message: response.message,
              type: 'error'
            });
          } else {
            if (this.isIbtPoiEditable) {
              ibtStore.dispatch('setStep', ibtStep.confirmId);
            } else {
              if (this.isIbtPoaEditable) {
                ibtStore.dispatch('setStep', ibtStep.confirmPoa);
              } else {
                ibtStore.dispatch('setStep', ibtStep.ibtStatus);
              }
            }
          }
        } else {
          console.log('error submit!!');
        }
      });
    },
    onSelectChange(index) {
      if (this.$refs[`collapseRowRef${index}`][0]) {
        this.$refs[`collapseRowRef${index}`][0].isCollapse = true;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.el-radio {
  margin-right: 0 !important;
}
.el-select{
  margin: 5px 0 16px 0;
}
</style>
